import { Link, NavLink, Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  DEFAULT_HELP_SECTION_ID,
  HELP_SECTIONS,
  HELP_SECTION_MAP,
} from "@/content/help";
import { HelpArticle } from "@/components/molecules/HelpArticle";
import { AppLogo } from "@/components/atoms/AppLogo";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { AppHeader } from "@/components/layout/AppHeader";
import { useAuth } from "@/hooks/useAuth.hook";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-2 rounded-[14px] px-3 py-2.5 text-sm font-semibold transition-colors ${
    isActive
      ? "bg-brand/15 text-brand ring-1 ring-brand/30"
      : "text-white/70 hover:bg-surface/10 hover:text-white"
  }`;

function HelpSidebar({ backHref, backLabel }: { backHref: string; backLabel: string }) {
  return (
    <aside className="lg:w-72 lg:shrink-0">
      <div className="rounded-[20px] bg-surface-elevated p-4 ring-1 ring-white/[0.09] lg:sticky lg:top-24">
        <Link
          to={backHref}
          className="inline-flex items-center gap-2 text-sm font-semibold text-white/55 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>

        <div className="mt-5 border-t border-white/10 pt-5">
          <div className="text-[11px] font-black uppercase tracking-wide text-white/45">
            Help center
          </div>
          <p className="mt-1 text-sm text-white/55">
            UI guides, data model, and calculation reference.
          </p>
        </div>

        <nav className="mt-4 max-h-[60vh] space-y-1 overflow-y-auto pr-1">
          {HELP_SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <NavLink
                key={section.id}
                to={`/help/${section.id}`}
                className={navLinkClass}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate">{section.title}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}

export function HelpIndexRedirect() {
  return <Navigate to={`/help/${DEFAULT_HELP_SECTION_ID}`} replace />;
}

export function HelpArticleSection() {
  const { sectionId } = useParams<{ sectionId: string }>();
  const section =
    (sectionId ? HELP_SECTION_MAP[sectionId] : undefined) ??
    HELP_SECTION_MAP[DEFAULT_HELP_SECTION_ID];

  if (!section) {
    return <Navigate to={`/help/${DEFAULT_HELP_SECTION_ID}`} replace />;
  }

  const Icon = section.icon;

  return (
    <div className="rounded-[20px] bg-surface-elevated p-6 ring-1 ring-white/[0.09]">
      <div className="mb-6 flex items-start gap-3 border-b border-white/10 pb-5">
        <div className="rounded-[12px] bg-brand/15 p-2.5">
          <Icon className="h-5 w-5 text-brand" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-white">{section.title}</h1>
          <p className="mt-1 text-sm text-white/60">{section.description}</p>
        </div>
      </div>
      <HelpArticle blocks={section.blocks} />
    </div>
  );
}

export function HelpLayout() {
  const navigate = useNavigate();
  const { userProfile, firebaseUser, signOut } = useAuth();
  const isAuthenticated = Boolean(firebaseUser);

  return (
    <div className="flex min-h-screen flex-col bg-page text-white">
      {isAuthenticated ? (
        <AppHeader
          displayName={userProfile?.displayName}
          onSettingsClick={() => navigate("/settings/startup")}
          onSignOut={signOut}
        />
      ) : (
        <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-page/95 backdrop-blur-md">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
            <AppLogo to="/" size={32} />
            <Link
              to="/signin"
              className="rounded-[10px] border border-white/15 px-3 py-1.5 text-sm font-semibold text-white/80 hover:bg-surface/10"
            >
              Sign in
            </Link>
          </div>
        </header>
      )}

      <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-black text-white">Help & transparency</h1>
          <p className="mt-1 max-w-2xl text-sm text-white/60">
            Everything BurnChart models — how the UI maps to your data, what each
            field means, and the formulas behind charts, runway, and funding
            scenarios.
          </p>
        </div>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
          <HelpSidebar
            backHref={isAuthenticated ? "/dashboard" : "/"}
            backLabel={isAuthenticated ? "Back to dashboard" : "Back to home"}
          />
          <main className="min-w-0 flex-1">
            <Outlet />
          </main>
        </div>
      </div>

      <SiteFooter variant="compact" className="mt-auto" />
    </div>
  );
}
